'use client'

import { useState } from 'react'
import type { GradeShareData, GradingProvider } from '../lib/types'
import { GRADING_PROVIDERS } from '../lib/types'
import { formatGrade } from '../lib/format'
import { topConfidencePercent } from '../lib/grade'
import { Slab } from './slab/Slab'
import { ReportHeader } from './ReportHeader'
import { GradeSummary } from './GradeSummary'
import { GradeAnalysisCard } from './GradeAnalysisCard'
import { ClosestMatchCard } from './ClosestMatchCard'
import { ReturnsBreakdown } from './ReturnsBreakdown'
import { ReportFooter } from './ReportFooter'
import { StickyCta } from './StickyCta'
import { GetAppDialog } from './GetAppDialog'
import styles from './report.module.css'

export function GradeReport({ data }: { data: GradeShareData }) {
  const providers = GRADING_PROVIDERS.filter((p) => data.providerGrades[p])
  const [provider, setProvider] = useState<GradingProvider>(
    providers[0] ?? 'PSA',
  )
  const [appOpen, setAppOpen] = useState(false)

  const result = data.providerGrades[provider]
  if (!result) return null

  return (
    <>
    <main className="page-shell">
      <ReportHeader data={data} />

      <div className={styles.columns}>
        <div className={styles.slabPanel}>
          <Slab
            frontImageUrl={data.card.frontImageUrl}
            backImageUrl={data.card.backImageUrl}
            frontImageRectificationApplied={data.card.frontImageRectificationApplied}
            backImageRectificationApplied={data.card.backImageRectificationApplied}
            embossFrontImageUrl={data.card.embossFrontImageUrl}
            embossBackImageUrl={data.card.embossBackImageUrl}
            cardName={data.card.name}
            cardNumber={data.card.number}
            officialCount={data.card.officialCount}
            setName={data.card.setName}
            language={data.card.language}
            grade={formatGrade(result.grade, provider)}
            provider={provider}
            confidencePercent={topConfidencePercent(result.confidence_map)}
            paletteColor={data.card.paletteColor}
            analysis={data.analysis}
            centering={data.centering}
          />
        </div>

        <section
          className={`${styles.card} ${styles.summaryPanel}`}
          aria-label="Grade prediction and analysis"
        >
          <GradeSummary
            providers={providers}
            provider={provider}
            onSelectProvider={setProvider}
            result={result}
            embedded
          />
          <div className={styles.analysisDivider} />
          <GradeAnalysisCard data={data} embedded />
        </section>

        <div className={styles.returnsPanel}>
          <ReturnsBreakdown
            data={data}
            provider={provider}
            result={result}
            inputs={data.profit[provider]}
          />
        </div>

        <div className={styles.closestPanel}>
          <ClosestMatchCard data={data} />
        </div>

        <p className={`${styles.disclaimer} ${styles.disclaimerPanel}`}>
          Evo uses custom AI models to predict grades, trained off real slab
          data. AI can make mistakes, so prediction results may vary.
        </p>
      </div>

    </main>
    <ReportFooter />
    <StickyCta user={data.user} onGetApp={() => setAppOpen(true)} />
    <GetAppDialog
      open={appOpen}
      onClose={() => setAppOpen(false)}
      user={data.user}
      code={data.user.referralCode ?? ''}
    />
    </>
  )
}
